import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  filter,
  map,
  switchMap,
  debounceTime,
  distinctUntilChanged,
  from,
  of,
  catchError,
  finalize,
  tap,
} from 'rxjs';
import { ButtonComponent } from '@aprova-digital/design-system';

import { Types } from '../../../../../domain/types.domain';
import { GetCepDetailsOutput } from '../../../../../domain/city/dtos/get-cep-details.output';
import {
  CheckboxComponent,
  InputComponent,
  LoadingSpinnerComponent,
  RadioComponent,
  RecaptchaComponent,
} from '../../../../../@ads/components';

import { SearchCepProviderInstruction } from '../../../../../builder/instructions/providers/search-cep.provider.instruction';

import { AccountFormHandler } from '../../account-form.handler';

@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RadioComponent,
    InputComponent,
    CheckboxComponent,
    ButtonComponent,
    RecaptchaComponent,
    LoadingSpinnerComponent,
  ],
  providers: [new SearchCepProviderInstruction()],
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.scss',
})
export class AddressFormComponent {
  private readonly _searchCepProvider = inject(Types.SearchCepProvider);
  private readonly _accountFormHandler = inject(AccountFormHandler);

  @Input()
  public isLoading: boolean = false;

  @Input()
  public hideRecaptcha: boolean = false;

  @Input()
  public blockedFields: string[] = [];

  @Input()
  public isLastStep: boolean = false;

  @Output()
  public setStepValidation = new EventEmitter<boolean>();

  @Output()
  public changeRecaptcha = new EventEmitter<string>();

  @ViewChild('inputStreet', { static: false })
  public inputStreet: InputComponent | undefined;

  @ViewChild('inputNumber', { static: false })
  public inputNumber: InputComponent | undefined;

  @ViewChild('inputNeighborhood', { static: false })
  public inputNeighborhood: InputComponent | undefined;

  public loading = signal(false);
  public hasError = signal(false);
  public errorMessage = signal('');
  public cepNotice = signal<'none' | 'not_found' | 'unavailable'>('none');

  public noNumberAddress = signal(false);
  private isUpdatingFromCep = false;

  public form = new FormGroup({
    cep: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    rua: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    estado: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    cidade: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    bairro: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    numero: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    complemento: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(144)],
    }),
    termos: new FormControl(false),
    recaptchaReactive: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    this.bindCepFields();

    if (this.isLastStep) {
      this.form.controls.termos.setValidators([Validators.requiredTrue]);
      this.form.controls.termos.setValue(false);

      if (!this.hideRecaptcha) {
        this.form.registerControl(
          'recaptchaReactive',
          new FormControl('', {
            nonNullable: true,
            validators: [Validators.required],
          })
        );
      }
    }

    this.form.valueChanges.subscribe((val) => {
      this.setStepValidation.emit(this.form.valid);
      const raw = this.form.getRawValue();
      this._accountFormHandler.setAddressInfo({
        cep: raw.cep,
        rua: raw.rua,
        estado: raw.estado,
        cidade: raw.cidade,
        bairro: raw.bairro,
        numero: raw.numero,
        complemento: raw.complemento ?? '',
        noNumberAddress: this.noNumberAddress(),
      });

      if (!this.hideRecaptcha && val.recaptchaReactive) {
        this.changeRecaptcha.emit(val.recaptchaReactive);
      }
    });

    const addressData = this._accountFormHandler.getAddressInfo();
    if (addressData) {
      this.isUpdatingFromCep = true;
      this.form.patchValue({
        cep: addressData.cep ?? '',
        rua: addressData.rua ?? '',
        estado: addressData.estado ?? '',
        cidade: addressData.cidade ?? '',
        bairro: addressData.bairro ?? '',
        numero: addressData.numero ?? '',
        complemento: addressData.complemento ?? '',
      });
      this.isUpdatingFromCep = false;
      const shouldRestoreNoNumber = addressData.noNumberAddress === true;
      if (shouldRestoreNoNumber) {
        this.noNumberAddress.set(true);
        this.applyNoNumberState();
      }
    }

    this.form.updateValueAndValidity();
  }

  private applyNoNumberState(): void {
    const numeroControl = this.form.get('numero');
    numeroControl?.setValue('');
    numeroControl?.setValidators([]);
    numeroControl?.disable();
    numeroControl?.updateValueAndValidity();
  }

  isFieldBlocked(name: string): boolean {
    return this.blockedFields.includes(name);
  }

  toogleNoNumberAddress(): void {
    this.noNumberAddress.update((val) => !val);
    const numeroControl = this.form.get('numero');

    if (this.noNumberAddress()) {
      numeroControl?.setValue('');
      numeroControl?.setValidators([]);
      numeroControl?.disable();
    } else {
      numeroControl?.enable();
      numeroControl?.setValue('');
      numeroControl?.setValidators([Validators.required, Validators.maxLength(144)]);
    }

    numeroControl?.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  private bindCepFields(): void {
    this.form.controls.cep.valueChanges
      .pipe(
        filter(() => !this.isUpdatingFromCep),
        tap(() => this.cepNotice.set('none')),
        map((cep) => cep.replace('-', '')),
        filter((cep) => cep.length === 8),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((cep) => {
          this.loading.set(true);
          return from(this._searchCepProvider.handle(cep)).pipe(
            catchError(() =>
              of<GetCepDetailsOutput>({ error: 'unavailable' })
            ),
            finalize(() => this.loading.set(false))
          );
        })
      )
      .subscribe((address: GetCepDetailsOutput) => {
        if (address.error) {
          this.cepNotice.set(address.error);
          return;
        }

        this.cepNotice.set('none');

        this.isUpdatingFromCep = true;
        const patch: Record<string, string> = {};
        if (address.rua) patch['rua'] = address.rua;
        if (address.cidade) patch['cidade'] = address.cidade;
        if (address.estado) patch['estado'] = address.estado;
        if (address.bairro) patch['bairro'] = address.bairro;
        this.form.patchValue(patch);
        this.isUpdatingFromCep = false;

        const inputComponent = this.getNextAddressInputToFill(address);
        inputComponent?.inputRef?.nativeElement.focus();
      });
  }

  private getNextAddressInputToFill(
    address: GetCepDetailsOutput
  ): InputComponent | undefined {
    if (!address.rua) {
      return this.inputStreet;
    }

    if (!address.bairro) {
      return this.inputNeighborhood;
    }

    return this.inputNumber;
  }
}
